import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Table,
} from 'antd';
import { get } from 'lodash';

import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import DiscountService from 'services/discount';
import { priceFormat } from 'utils';

import './style.scss';
import queryString from 'query-string';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;

const FIELDS = {
  DESKTOP_MEDIA_URL: 'banner_image',
  MOBILE_MEDIA_URL: 'mobile_image',
};

export function Discount() {
  const [form] = Form.useForm();
  const [discountList, setDiscountList] = useState([]);
  const [discountProp, setDiscountProp] = useState({});
  const [discountPropModal, setDiscountPropModal] = useState({});
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadStatus, setLoadStatus] = useState(true);
  const [maxPrice, setMaxPrice] = useState(0);
  const [minPrice, setMinPrice] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showModalBanner, setShowModalBanner] = useState(false);
  const [sorting, setSorting] = useState('created_at desc');

  const location = useLocation();
  const parsed = queryString.parse(location.search);
  const navigate = useNavigate();

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  const customElement = (row) => (
    <div className="button-table">
      <Row>
        <Col span={12}>
          <Link
            to={{
              pathname: '/products/edit',
              search: `id=${row.id}`,
              state: { prevPath: '/discounts', parsed },
            }}>
            <PdButton className="custom-button-table w-90" type="primary">
              Edit
            </PdButton>
          </Link>
        </Col>
      </Row>
    </div>
  );

  const coloredStatus = (row) => (
    <div className="status-indicator">
      <span className={row.status !== 'inactive' ? 'active' : 'hidden'}>
        {displayStatus(row.status)}
      </span>
    </div>
  );

  const price = (row) => (
    <div className="price-container pricing">
      <div className="fs-14 spacing">
        IDR {priceFormat(row.discounted_price)}
      </div>
      <strike className="fs-12 spacing">IDR {priceFormat(row.price)}</strike>
    </div>
  );

  const title = (row) => (
    <div className="product-container">
      <div className="table-title--data spacing fs-14">{row.title}</div>
      {row.sku !== null && <div className="fs-12">SKU: {row.sku}</div>}
    </div>
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '10%',
    },
    {
      title: 'Title',
      key: 'title',
      width: '42%',
      render: (_text, record) => title(record),
    },
    {
      title: 'Base Price',
      key: 'price',
      width: '15%',
      render: (_text, record) => price(record),
    },
    {
      title: 'Status',
      key: 'status',
      width: '19%',
      align: 'center',
      render: (_text, record) => coloredStatus(record),
    },
    {
      title: '',
      key: 'action',
      width: '25%',
      render: (_text, record) => customElement(record),
    },
  ];

  async function fetchDiscountList(
    page = 1,
    q = '',
    sort_by,
    total = 10,
    status,
    minPrice,
    maxPrice
  ) {
    setLoadStatus(true);
    try {
      const { data } = await DiscountService.getProductList(
        page,
        q,
        sort_by,
        total,
        minPrice,
        maxPrice,
        status
      );
      const productData = data.data;
      const metadata = data.metadata;

      setTotalItems(metadata.total_items);
      setLoadStatus(false);
      setDiscountList(productData);
    } finally {
      setLoadStatus(false);
    }
  }

  async function getDiscountProperties() {
    const { data } = await DiscountService.getDiscountProp();
    const discountData = data.data;
    setDiscountProp(discountData);
    setDiscountPropModal(discountData);
  }

  function handleTableChange(pagination) {
    if (pagination.current < 1) return;
    parsed['page'] = pagination.current;
    parsed['pageSize'] = pagination.pageSize;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/discounts',
      search: stringified,
    });
  }

  function filterProduct(param, val) {
    if (!val) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/discounts',
      search: stringified,
    });
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function closeModal() {
    setDiscountPropModal(discountProp);
    setShowModalBanner(false);
  }

  async function handleSubmit(values) {
    try {
      setLoadingButton(true);
      await DiscountService.updateDiscountProp({
        ...discountPropModal,
        ...values,
      });
      message.success('Successfully update banner');
      getDiscountProperties();
      setShowModalBanner(false);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function changeSearchValue(e) {
    setSearchValue(e.target.value);
  }

  function changeMinPrice(e) {
    setMinPrice(e === null ? 0 : e);
  }

  function changeMaxPrice(e) {
    setMaxPrice(e === null ? 0 : e);
  }

  function handleChangeDiscountPropModal(url, key) {
    setDiscountPropModal((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  /* eslint-disable */
  useEffect(() => {
    if (parsed['title']) {
      setSearchValue(parsed['title']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/discounts',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    if (parsed['pageSize']) {
      setPageSize(Number(parsed['pageSize']));
    } else {
      setPageSize(10);
    }

    if (parsed['sortBy']) {
      setSorting(parsed['sortBy']);
    } else {
      setSorting('created_at desc');
    }

    if (parsed['status']) {
      setSelectedStatus(parsed['status']);
    } else {
      setSelectedStatus('');
    }

    if (parsed['minPrice']) {
      if (parsed['minPrice'] < 1) {
        delete parsed['minPrice'];
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/discounts',
          search: stringified,
        });
      } else {
        setMinPrice(parsed['minPrice']);
      }
    } else {
      setMinPrice(0);
    }

    if (parsed['maxPrice']) {
      if (parsed['maxPrice'] < 1) {
        delete parsed['maxPrice'];
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/discounts',
          search: stringified,
        });
      } else {
        setMaxPrice(parsed['maxPrice']);
      }
    } else {
      setMaxPrice(0);
    }

    fetchDiscountList(
      parsed['page'],
      parsed['title'],
      parsed['sortBy'],
      parsed['pageSize'] || 10,
      parsed['status'],
      parsed['minPrice'],
      parsed['maxPrice']
    );
  }, [
    parsed['title'],
    parsed['page'],
    parsed['pageSize'],
    parsed['sortBy'],
    parsed['status'],
    parsed['minPrice'],
    parsed['maxPrice'],
  ]);
  /* eslint-enable */
  useEffect(() => {
    getDiscountProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="pd-cms-discounts main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Discounts</div>
          <Content className="pageContent">
            <Row className="banner-container" gutter={[60, 10]}>
              <Col xs={24}>
                <span className="title-banner">Banner Image</span>
              </Col>
              <Col xs={14}>
                <span className="desc-banner">Desktop (1200x200 px)</span>
                <div
                  className="preview-banner"
                  style={
                    discountProp.banner_image && {
                      backgroundColor: '#fff',
                      justifyContent: 'flex-start',
                    }
                  }>
                  {discountProp.banner_image ? (
                    <img
                      alt="img"
                      className="preview-img"
                      src={discountProp.banner_image}
                    />
                  ) : (
                    <span className="img-alt">No image available</span>
                  )}
                </div>
              </Col>
              <Col xs={10}>
                <span className="desc-banner">Mobile (640x320 px)</span>
                <div
                  className="preview-banner mobile"
                  style={
                    discountProp.banner_image && {
                      backgroundColor: '#fff',
                      justifyContent: 'flex-start',
                    }
                  }>
                  {discountProp.mobile_image ? (
                    <img
                      className="preview-img"
                      alt="img"
                      src={discountProp.mobile_image}
                    />
                  ) : (
                    <span className="img-alt">No image available</span>
                  )}
                </div>
              </Col>
              <Col xs={24}>
                <PdButton
                  className="custom-button w-10"
                  type="primary"
                  onClick={setShowModalBanner.bind(this, true)}>
                  Edit
                </PdButton>
              </Col>
            </Row>
            <Row>
              <Col xs={18}>
                <Search
                  className="search-product"
                  enterButton
                  onChange={changeSearchValue}
                  onSearch={filterProduct.bind(this, 'title')}
                  placeholder="Search Product List"
                  value={searchValue}
                />
              </Col>

              <Col xs={24} className="mt-1 filter-container">
                <div className="box-filter">
                  <span>Price </span>
                  <span>Min : </span>
                  <InputNumber
                    className="min-max-input"
                    formatter={(value) =>
                      `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    min={0}
                    onBlur={filterProduct.bind(this, 'minPrice', minPrice)}
                    onChange={changeMinPrice}
                    parser={(value) =>
                      value.replace(/[^0-9]\s?|(\.*)/g, '').replace(/^0+/, '')
                    }
                    placeholder="min price"
                    step={1}
                    value={minPrice}
                  />
                </div>

                <div className="box-filter">
                  <span> Max : </span>
                  <InputNumber
                    className="min-max-input"
                    formatter={(value) =>
                      `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    min={0}
                    onBlur={filterProduct.bind(this, 'maxPrice', maxPrice)}
                    onChange={changeMaxPrice}
                    parser={(value) =>
                      value.replace(/[^0-9]\s?|(\.*)/g, '').replace(/^0+/, '')
                    }
                    placeholder="max price"
                    step={1}
                    value={maxPrice}
                  />
                </div>

                <div className="box-filter">
                  <Select
                    className="custom-select"
                    value={selectedStatus}
                    onChange={filterProduct.bind(this, 'status')}>
                    <Option disabled value={null}>
                      Status
                    </Option>
                    <Option value="">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </div>

                <div className="box-filter">
                  <Select
                    value={sorting}
                    onChange={filterProduct.bind(this, 'sortBy')}>
                    <Option disabled value="">
                      Sort By
                    </Option>
                    <Option value="created_at desc">Release Date</Option>
                    <Option value="price asc">Lowest Price</Option>
                    <Option value="price desc">Highest Price</Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24}>
                <Table
                  dataSource={discountList}
                  columns={columns}
                  rowKey={(record) => record.id}
                  loading={loadStatus}
                  onChange={handleTableChange}
                  pagination={{
                    total: totalItems,
                    current: Number(page),
                    pageSize: pageSize,
                    showSizeChanger: true,
                  }}
                />
              </Col>
            </Row>
          </Content>
        </div>
      </div>
      <Modal
        centered
        className="modal--bannerImage"
        onCancel={closeModal}
        onOk={closeModal}
        title="Edit Banner"
        open={showModalBanner}>
        <div className="modal-container">
          <Form form={form} layout={formLayout} onFinish={handleSubmit}>
            <Form.Item label="Desktop Image (1200x200 px)" {...formItemLayout}>
              <PdUploader
                afterUpload={handleChangeDiscountPropModal}
                imgClassName="w-full"
                imgUrl={discountPropModal[FIELDS.DESKTOP_MEDIA_URL]}
                keyValue={FIELDS.DESKTOP_MEDIA_URL}
                name="Desktop Image (1280x400 px)"
              />
            </Form.Item>
            <Form.Item label="Mobile Image (640x320 px)" {...formItemLayout}>
              <PdUploader
                afterUpload={handleChangeDiscountPropModal}
                imgClassName="w-full"
                imgUrl={discountPropModal[FIELDS.MOBILE_MEDIA_URL]}
                keyValue={FIELDS.MOBILE_MEDIA_URL}
                name="Mobile Image (640x320 px)"
              />
            </Form.Item>
            <Form.Item {...buttonItemLayout}>
              <div className="buttonSection mt-2">
                <PdButton
                  full
                  htmlType="submit"
                  loading={loadingButton}
                  type="primary">
                  Publish
                </PdButton>
              </div>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </Layout>
  );
}
